import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { App, Flex, Pagination, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../../Components/Button"
import EmptyComponent from "../../../../Components/Empty"
import ExportDropdown from "../../../../Components/Export"
import Filters from "../../../../Components/Filters"
import MobilePagination from "../../../../Components/MobilePagination/MobilePagination"
import { SubtitleSection } from "../../../../Components/SubtitleSection/SubtitleSection"
import Table from "../../../../Components/Table"
import { useIncognito } from "../../../../hooks/useIncognito"
import { serializeData } from "../../../../Utils/utils"
import { usePageQuery } from "../../../BusinessSettings/usePageQuery"
import UserActions from "../../../User/reducer"
import Creators, { selectAreFiltersDefault } from "../../reducer"
import BalanceActivityCard from "./BalanceActivityCard"
import { getColumns, getDefaultColumn } from "./columns"

import "./styles.less"

//import ButtonComponent from '../../../Components/Button'

const pageSize = 10

const { Title, Text } = Typography

const BalanceActivity = ({
  isMobile,
  isAdmin,
  theme,
  userInfo,
  current_business_account_id,

  columns,
  fetching,
  result,
  exportFetching,
  exportResult,
  generateInvoice,
  userViewForAdmin,

  filters,
  filterOptions,
  visibleFilters,
  defaultColumns,

  saveResult,
  ...props
}) => {
  //const navigate = useNavigate()
  const { search } = useLocation()
  const { user_id } = usePageQuery(search)
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()
  const navigate = useNavigate()
  const incognito = useIncognito()
  const pendingUrlRef = useRef(null)

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)
  const [showAll, setShowAll] = useState(false)

  const isFiltersDefault = useSelector(selectAreFiltersDefault)

  const { count } = result

  const results = showAll ? result?.results : [...result?.results]?.slice(0, 5)

  const myColumns = columns

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")

  const businessAccountId = current_business_account_id

  const notificationConfig = {
    closeIcon: <Text>Dismiss</Text>,
    style: { minWidth: "max-content", width: 475 }
  }

  const updateData = () => {
    const serializeFilters = serializeData(filters)

    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...serializeFilters,
      //...(user_id ? {user_id} : {business_account_id: businessAccountId}),
      user_id: user_id || userInfo?.id
      //columns: ['sequence_number', 'id', 'status', 'brand', ...columns].join(',')
    }
    props.getBalanceActivity({ ...data })
  }

  useEffect(() => {
    props.getBalanceActivityFilterOptions()
  }, [])

  useEffect(() => {
    updateData()
  }, [businessAccountId, ordering, pageNumber, myColumnsLength, filters])

  useEffect(() => {
    if (exportResult) {
      notification.success({
        message: t("Exporting"),
        description: t("Payment activity table will be downloaded shortly."),
        ...notificationConfig
      })
      props.clearExportResult()
    }
  }, [exportResult])

  const tableSettings = useMemo(
    () => ({
      scroll: {
        x: "100%",
        y: "calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px))",
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      },
      locale: {
        emptyText: !count ? (
          <EmptyComponent type="no_data_available" />
        ) : (
          <EmptyComponent type="search" />
        )
      }
    }),
    [myColumnsLength, count, pageNumber, showAll]
  )

  const handleNavigate = useCallback(
    (url, record_ba_id) => {
      if (businessAccountId !== record_ba_id) {
        pendingUrlRef.current = url
        props.changeBusinessAccount(record_ba_id)
      } else {
        navigate(url)
      }
    },
    [businessAccountId, navigate, props]
  )

  useEffect(() => {
    if (saveResult) {
      //after change business account in handleNavigate
      notification.success({
        message: t("Business account changed"),
        // description: t('You have successfully switched business accounts.'),
        ...notificationConfig
      })
      if (pendingUrlRef.current) {
        navigate(pendingUrlRef.current)
        pendingUrlRef.current = null
      }
      props.userSaveResultReset()
    }
  }, [saveResult, navigate])

  const getTableColumns = useMemo(() => {
    let allColumns = getColumns({ t, theme, handleNavigate, incognito })
    return [
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data }))
      //getActionColumn({onSelectRow: handleSelectRow})
    ]
  }, [myColumnsHash, incognito])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleExport = () => {
    if (isAdmin) return
    if (user_id || userInfo?.id) {
      props.getBalanceActivityExport({
        file_name: "Balance activity",
        //business_account_id: businessAccountId,
        user_id: user_id || userInfo?.id
      })
    }
  }

  const handleChangeFilter = useCallback((newData, type) => {
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFilters", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
  }, [])

  const onClear = useCallback(() => {
    props.clearBalanceFilters()
  }, [])

  if (isMobile) {
    const allMobileData = result?.results ?? []
    const mobileData = showAll ? allMobileData : allMobileData.slice(0, 4)

    return (
      <>
        <SubtitleSection
          isMobile
          title={t("Payment activity")}
          description={t("View all the payment activity and their statuses.")}
        />

        <ExportDropdown
          loading={exportFetching}
          onExport={handleExport}
          isAdmin={false}
          disabled={!allMobileData?.length || isAdmin}
          block
          fullWidths
        />

        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={defaultColumns}
          filtersType="payment_activity"
          filters={filters}
          onChangeFilter={handleChangeFilter}
          showClearBtn={!isFiltersDefault}
          onClearFilters={onClear}
          isMobile={isMobile}
        />

        <Spin spinning={fetching}>
          <Flex vertical gap={12} style={{ marginTop: 16 }}>
            {allMobileData.length === 0 && !fetching && <EmptyComponent type="no_data_available" />}
            {mobileData.map((record, i) => (
              <BalanceActivityCard key={record?.id ?? i} record={record} theme={theme} />
            ))}
          </Flex>
        </Spin>

        {count > 4 && !showAll && (
          <Flex justify="center" style={{ marginTop: 16 }}>
            <ButtonComponent onClick={() => setShowAll(true)} text={t("View all activity list")} />
          </Flex>
        )}

        {showAll &&
          count > pageSize &&
          (isMobile ? (
            <MobilePagination
              current={pageNumber}
              total={count}
              pageSize={pageSize}
              onChange={(page) => {
                setPageNumber(page)
                setOrdering(null)
              }}
            />
          ) : (
            <Flex justify="center" style={{ marginTop: 16 }}>
              <Pagination
                total={count}
                current={pageNumber}
                pageSize={pageSize}
                onChange={(page) => {
                  setPageNumber(page)
                  setOrdering(null)
                }}
                showSizeChanger={false}
              />
            </Flex>
          ))}
      </>
    )
  }

  return (
    <>
      <Flex justify="space-between">
        <SubtitleSection
          title={t("Payment activity")}
          description={t("View all the payment activity and their statuses.")}
        />

        {!isMobile && (
          <ExportDropdown
            loading={exportFetching}
            onExport={handleExport}
            isAdmin={false}
            disabled={!results?.length || isAdmin}
          />
        )}
      </Flex>

      <Flex gap={32} align="center" justify="space-between" style={{ margin: "-4px 0 24px" }}>
        <Filters
          visibleFilters={visibleFilters}
          filterOptions={filterOptions}
          allFilters={defaultColumns}
          filtersType={"payment_activity"}
          filters={filters}
          onChangeFilter={handleChangeFilter}
          showClearBtn={!isFiltersDefault}
          onClearFilters={onClear}
          isMobile={isMobile}
        />
      </Flex>

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        withOutPagination={!showAll}
        locale={tableSettings.locale}
      />

      {count && count > 5 && !showAll ? (
        <Flex justify={"center"} align={"center"} style={{ marginTop: 16 }}>
          <ButtonComponent onClick={() => setShowAll(true)} text={t("View all activity list")} />
        </Flex>
      ) : null}
    </>
  )
}

const mapStateToProps = (state) => ({
  current_business_account_id: state.auth.userInfo.current_business_account_id,
  userInfo: state.auth.userInfo,
  isAdmin: state.auth.isAdmin,
  isMobile: state.nav.isMobile,
  theme: state.nav.theme,

  columns: state.balance.columns,
  fetching: state.balance.fetching,
  result: state.balance.result,

  exportFetching: state.balance.exportFetching,
  exportResult: state.balance.exportResult,

  filters: state.balance.filters,
  filterOptions: state.balance.filterOptions,
  visibleFilters: state.balance.visibleFilters,
  defaultColumns: state.balance.defaultColumns,

  saveResult: state.user.saveResult
})

const mapDispatchToProps = (dispatch) => ({
  getBalanceActivity: (params) => dispatch(Creators.getBalanceActivityRequest(params)),

  getBalanceActivityExport: (data) => dispatch(Creators.getBalanceActivityExportRequest(data)),
  clearExportResult: () => dispatch(Creators.balanceSetProp("exportResult", null)),

  getBalanceActivityFilterOptions: (params) =>
    dispatch(Creators.getBalanceActivityFilterOptionsRequest(params)),
  changeFilters: (filters) => dispatch(Creators.balanceSetProp("filters", filters)),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(Creators.balanceSetProp(visibleFiltersType, filters)),
  clearBalanceFilters: () => dispatch(Creators.clearBalanceFilters()),

  changeBusinessAccount: (newBusinessAccountId) =>
    dispatch(UserActions.userSaveRequest({ current_business_account_id: newBusinessAccountId })),
  userSaveResultReset: () => dispatch(UserActions.userSaveResultReset())
})

export default connect(mapStateToProps, mapDispatchToProps)(BalanceActivity)
